import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import {
  FiRefreshCw,
  FiWifi,
  FiWifiOff,
  FiClock,
  FiEdit2,
  FiSearch,
  FiPlus,
  FiList,
  FiInfo,
} from "react-icons/fi";
import { seedReportFilters } from "../Reports/shared/useReportFilters";
import {
  formatDateTimeTrInAppTz,
  formatTimeTrInAppTz,
  dateStrInAppTz,
} from "../../../utils/appTime";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Pagination from "../../../components/BaseUI/Pagination";
import ScrapEntryModal from "./modals/ScrapEntryModal";
import ProductionDetailsModal from "./modals/ProductionDetailsModal";
import ProductionEntryModal from "./modals/ProductionEntryModal";
import StopReasonModal from "./modals/StopReasonModal";
import StatusHistoryModal from "./modals/StatusHistoryModal";
import PeriodDateSelector from "../../../components/CustomUI/PeriodDateSelector";
import {
  createPeriodState,
  todayPeriodStr,
} from "../../../utils/periodDateUtils";
import "./MachineMonitor.scss";
import "./MachineMonitor.modals.scss";
import "../DeviceMonitor/DeviceMonitor.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
const LAST_MACHINE_STORAGE_KEY = "machine-monitor:lastMachineId";

function lastMachineStorageKey(user) {
  const uid = user?._id || user?.id;
  return uid ? `${LAST_MACHINE_STORAGE_KEY}:${uid}` : LAST_MACHINE_STORAGE_KEY;
}

function readLastMachineId(user) {
  try {
    const uid = user?._id || user?.id;
    if (uid) {
      const scoped = localStorage.getItem(`${LAST_MACHINE_STORAGE_KEY}:${uid}`);
      if (scoped) return scoped;
    }
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${LAST_MACHINE_STORAGE_KEY}:`)) {
        const v = localStorage.getItem(k);
        if (v) return v;
      }
    }
    return localStorage.getItem(LAST_MACHINE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function writeLastMachineId(user, machineId) {
  if (!machineId) return;
  try {
    localStorage.setItem(lastMachineStorageKey(user), String(machineId));
    if (user?._id || user?.id) {
      localStorage.removeItem(LAST_MACHINE_STORAGE_KEY);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

function pickMachineId(list, { navId, cachedId, prev }) {
  const ids = new Set((list || []).map((m) => String(m._id)));
  if (navId && ids.has(String(navId))) return String(navId);
  if (cachedId && ids.has(String(cachedId))) return String(cachedId);
  if (prev && ids.has(String(prev))) return String(prev);
  return list?.[0]?._id ? String(list[0]._id) : "";
}

/** Legacy value — mesai fazlası is now included in the last shift, not a separate mode. */
const OVERTIME_SHIFT_QUERY_ID = "overtime";

const formatDuration = (ms) => {
  if (ms == null || ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/** Overlap of [rangeStartMs, rangeEndMs] with planned segments (breaks excluded). */
const overlapMsWithSegments = (rangeStartMs, rangeEndMs, segments) => {
  if (!segments?.length) return 0;
  let sum = 0;
  for (const seg of segments) {
    const s = new Date(seg.start).getTime();
    const e = new Date(seg.end).getTime();
    sum += Math.max(0, Math.min(rangeEndMs, e) - Math.max(rangeStartMs, s));
  }
  return sum;
};

const plannedElapsedMsFromSegments = (nowMs, segments) => {
  if (!segments?.length) return 0;
  let sum = 0;
  for (const seg of segments) {
    const s = new Date(seg.start).getTime();
    const e = new Date(seg.end).getTime();
    sum += Math.max(0, Math.min(nowMs, e) - s);
  }
  return sum;
};

/** Live idle start — after net cycle + grace from last production (matches stop-log open). */
const virtualIdleFromMs = (
  nowMs,
  grossStartMs,
  segments,
  lastProductionAtMs,
  lastPlannedStopEndMs,
  idleGraceMs = 0,
) => {
  let idleFromMs = grossStartMs;

  if (lastPlannedStopEndMs != null) {
    idleFromMs = Math.max(idleFromMs, lastPlannedStopEndMs);
    if (
      lastProductionAtMs != null &&
      lastProductionAtMs > lastPlannedStopEndMs &&
      idleGraceMs > 0 &&
      nowMs <= lastPlannedStopEndMs + idleGraceMs
    ) {
      return idleFromMs;
    }
  }

  if (lastProductionAtMs != null) {
    idleFromMs = Math.max(idleFromMs, lastProductionAtMs + idleGraceMs);
  } else if (lastPlannedStopEndMs == null) {
    let shiftStart = grossStartMs;
    for (const seg of segments) {
      const a = new Date(seg.start).getTime();
      const b = new Date(seg.end).getTime();
      if (nowMs >= a && nowMs < b) {
        shiftStart = Math.max(shiftStart, a);
        break;
      }
    }
    idleFromMs = Math.max(idleFromMs, shiftStart + idleGraceMs);
  }

  return idleFromMs;
};

const DonutChart = ({
  percentage = 0,
  size = 160,
  strokeWidth = 18,
  color = "#00c9b7",
  label,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const safePerc = Math.max(0, Math.min(100, percentage));
  const offset = circumference - (safePerc / 100) * circumference;

  return (
    <div className="donut-wrap">
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--m-surface-subtle, #e0f7f5)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
      {label && (
        <div className="donut-label" style={{ transform: "none" }}>
          {label}
        </div>
      )}
    </div>
  );
};

const GaugeCircle = ({
  value,
  max = 100,
  label,
  unit = "",
  color = "#00c9b7",
}) => {
  const size = 88;
  const sw = 8;
  const radius = (size - sw) / 2;
  const circumference = 2 * Math.PI * radius;
  const safeVal = value == null ? 0 : Math.max(0, Math.min(max, value));
  const perc = max > 0 ? safeVal / max : 0;
  const offset = circumference - perc * circumference;

  return (
    <div className="gauge-circle-wrap">
      <div className="gauge-circle-svg-wrap">
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--m-surface-subtle, #e0f7f5)"
            strokeWidth={sw}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={sw}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 1s ease" }}
          />
        </svg>
        <div className="gauge-circle-value">
          {value == null ? "—" : Math.round(value)}
          {unit && <span className="gauge-unit">{unit}</span>}
        </div>
      </div>
      <div className="gauge-label">{label}</div>
    </div>
  );
};

const MachineMonitor = () => {
  const {
    getToken,
    isAdmin,
    hasPageWriteAccess,
    user,
    loading: authLoading,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedMachineId, setSelectedMachineId] = useState(
    () => location?.state?.machineId || readLastMachineId(user) || "",
  );
  const [selectedShiftId, setSelectedShiftId] = useState("");
  const [period, setPeriod] = useState(() => createPeriodState("day"));
  const selectedDate = period.startDate;
  const isLiveDay = selectedDate === todayPeriodStr();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [breaksModalOpen, setBreaksModalOpen] = useState(false);
  const [partEditOpen, setPartEditOpen] = useState(false);
  const [cycleEditOpen, setCycleEditOpen] = useState(false);
  const [cycleDraftNet, setCycleDraftNet] = useState("");
  const [cycleDraftQty, setCycleDraftQty] = useState("1");
  const [savingCycle, setSavingCycle] = useState(false);
  const [cycleEditError, setCycleEditError] = useState("");
  const [partMachines, setPartMachines] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [productTypeDraftCodes, setProductTypeDraftCodes] = useState([]);
  const [savingProductType, setSavingProductType] = useState(false);
  const [productTypePage, setProductTypePage] = useState(1);
  const [partSearchQuery, setPartSearchQuery] = useState("");
  const [stopsSortMode, setStopsSortMode] = useState("biggest");
  const [entryModal, setEntryModal] = useState(null);
  const [productionDetailsOpen, setProductionDetailsOpen] = useState(false);
  const [statusHistoryOpen, setStatusHistoryOpen] = useState(false);

  // Live clock offset: ms difference between server time and client time at fetch
  const serverOffsetRef = useRef(0);
  const [tick, setTick] = useState(0);
  const tickRef = useRef(null);
  const statsRef = useRef(null);
  const fetchStatsRef = useRef(null);
  const pollRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const mountedRef = useRef(true);
  const tokenRef = useRef(getToken());
  const prevWorkStateKindRef = useRef(null);
  const selectedMachineIdRef = useRef(selectedMachineId);
  const selectedShiftIdRef = useRef(selectedShiftId);
  const selectedDateRef = useRef(selectedDate);
  const machinesRef = useRef(machines);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Tick every second for live durations
  useEffect(() => {
    tickRef.current = setInterval(() => {
      if (mountedRef.current) setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const fetchMachineList = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_URL}/api/machine-live/list-machines`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setMachines(res.data.machines || []);
        setShifts(res.data.shifts || []);

        const list = res.data.machines || [];
        const navId = location?.state?.machineId
          ? String(location.state.machineId)
          : "";
        const cachedId = readLastMachineId(user);
        setSelectedMachineId((prev) =>
          pickMachineId(list, { navId, cachedId, prev }),
        );
      }
    } catch (e) {
      console.error("Failed to fetch machine list", e);
    }
  }, [getToken, location?.state?.machineId, user]);

  useEffect(() => {
    if (selectedMachineId) writeLastMachineId(user, selectedMachineId);
  }, [selectedMachineId, user]);

  const fetchStats = useCallback(
    async (machineId, shiftId, dateStr) => {
      if (!machineId) return;
      const date = dateStr || selectedDateRef.current || todayPeriodStr();
      const live = date === todayPeriodStr();
      setLoading(true);
      try {
        const token = getToken();
        const params = {};
        const effectiveShiftId =
          shiftId === OVERTIME_SHIFT_QUERY_ID ? "" : shiftId;
        if (effectiveShiftId) params.shiftId = effectiveShiftId;
        if (!live) params.date = date;
        const headers = { Authorization: `Bearer ${token}` };
        const listMachine = (machinesRef.current || []).find(
          (m) => String(m._id) === String(machineId),
        );
        const isEpcMachine =
          listMachine?.entryType === "epc" ||
          statsRef.current?.machine?.entryType === "epc";
        if (live && !isEpcMachine) {
          await axios.post(
            `${API_URL}/api/machine-live/${machineId}/sync-stops`,
            {},
            { headers, params: { shiftId: effectiveShiftId || undefined } },
          );
        }
        const res = await axios.get(
          `${API_URL}/api/machine-live/${machineId}/${live ? "live-stats" : "report-stats"}`,
          {
            headers,
            params,
          },
        );
        if (res.data.success) {
          const data = res.data.data;
          if (data.serverTime) {
            serverOffsetRef.current =
              new Date(data.serverTime).getTime() - Date.now();
          }
          setStats(data);
          statsRef.current = data;
          if (shiftId === OVERTIME_SHIFT_QUERY_ID) {
            setSelectedShiftId(data.selectedShift?._id || "");
          } else if (live && !shiftId && data.selectedShift) {
            setSelectedShiftId(data.selectedShift._id);
          }
        }
      } catch (e) {
        console.error("Failed to fetch stats", e);
      } finally {
        setLoading(false);
      }
    },
    [getToken],
  );

  // Keep a ref so the WS callback can always call the latest fetchStats
  useEffect(() => {
    fetchStatsRef.current = fetchStats;
  }, [fetchStats]);

  // Initial load — wait for auth so the user-scoped cache is used
  useEffect(() => {
    if (authLoading) return;
    fetchMachineList();
  }, [authLoading, fetchMachineList]);

  // Mesai fazlası is no longer a separate shift option — reset legacy selection.
  useEffect(() => {
    if (selectedShiftId === OVERTIME_SHIFT_QUERY_ID) {
      setSelectedShiftId("");
    }
  }, [selectedShiftId]);

  // Fetch stats when machine, shift, or day changes
  useEffect(() => {
    if (selectedMachineId) {
      fetchStats(selectedMachineId, selectedShiftId, selectedDate);
    }
  }, [selectedMachineId, selectedShiftId, selectedDate]);

  // Poll every 30 seconds (live day only)
  useEffect(() => {
    pollRef.current = setInterval(() => {
      if (
        selectedMachineId &&
        mountedRef.current &&
        selectedDateRef.current === todayPeriodStr()
      ) {
        fetchStats(selectedMachineId, selectedShiftId, selectedDateRef.current);
      }
    }, 30000);
    return () => clearInterval(pollRef.current);
  }, [selectedMachineId, selectedShiftId, fetchStats]);

  const closeWebSocket = useCallback((code = 1000, reason = "Cleanup") => {
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
    const ws = wsRef.current;
    if (!ws) return;
    wsRef.current = null;
    intentionalCloseRef.current = true;
    try {
      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        ws.close(code, reason);
      }
    } catch {}
  }, []);

  // WebSocket for live machine status (one connection per tab; server allows multiple per user)
  const connectWebSocket = useCallback(
    (token) => {
      if (!mountedRef.current || !token) return;

      const existing = wsRef.current;
      if (
        existing &&
        (existing.readyState === WebSocket.OPEN ||
          existing.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      closeWebSocket();
      intentionalCloseRef.current = false;

      const apiUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
      let wsUrl = apiUrl.replace(/^http/, "ws");
      if (!wsUrl.startsWith("ws://") && !wsUrl.startsWith("wss://")) {
        wsUrl = `ws://${wsUrl.replace(/^https?:\/\//, "")}`;
      }
      const wsPath = wsUrl.endsWith("/") ? "ws" : "/ws";
      const ws = new WebSocket(`${wsUrl}${wsPath}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current || wsRef.current !== ws) {
          ws.close(1000, "Stale connection");
          return;
        }
        setWsConnected(true);
      };
      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        if (!mountedRef.current) return;
        setWsConnected(false);
        if (intentionalCloseRef.current) return;
        reconnectRef.current = setTimeout(() => {
          if (mountedRef.current && !intentionalCloseRef.current) {
            connectWebSocket(tokenRef.current);
          }
        }, 5000);
      };
      ws.onmessage = (event) => {
        if (!mountedRef.current || wsRef.current !== ws) return;
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "ping") {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: "pong", timestamp: Date.now() }));
            }
            return;
          }
          if (msg.type !== "telemetry") return;

          const currentStats = statsRef.current;
          if (!currentStats) return;
          const currentMachineId = String(currentStats.machine?._id || "");

          if (msg.deviceId === "machine_update" && msg.data?.machines) {
            const m = msg.data.machines.find(
              (mx) => String(mx._id) === currentMachineId,
            );
            if (!m) return;

            const prevStatus = currentStats.machine?.status;
            const statusChanged = m.status !== prevStatus;

            setStats((prev) => {
              if (!prev) return prev;
              // Access/status WS: only refresh status/heartbeat — never touch part fields here.
              // Part changes come from live-stats after PLC / product MQTT, or explicit save.
              return {
                ...prev,
                machine: {
                  ...prev.machine,
                  status: m.status,
                  lastHeartbeat: m.lastHeartbeat,
                },
              };
            });

            const prevPt = String(currentStats.machine?.productType || "")
              .trim()
              .toLowerCase();
            const nextPt = String(m.productType || "")
              .trim()
              .toLowerCase();
            const prevTypes = (currentStats.machine?.productTypes || [])
              .map((c) =>
                String(c || "")
                  .trim()
                  .toLowerCase(),
              )
              .filter(Boolean)
              .sort()
              .join(",");
            const nextTypes = (m.productTypes || [])
              .map((c) =>
                String(c || "")
                  .trim()
                  .toLowerCase(),
              )
              .filter(Boolean)
              .sort()
              .join(",");
            const productChanged =
              (Boolean(nextPt) && nextPt !== prevPt) ||
              (Boolean(nextTypes) && nextTypes !== prevTypes);
            if (statusChanged || productChanged) {
              if (selectedDateRef.current !== todayPeriodStr()) return;
              const sid = statsRef.current?.selectedShift?._id || "";
              fetchStatsRef.current?.(currentMachineId, sid);
            }
          }
        } catch {}
      };
    },
    [closeWebSocket],
  );

  useEffect(() => {
    tokenRef.current = getToken();
  }, [getToken]);

  useEffect(() => {
    machinesRef.current = machines;
  }, [machines]);

  useEffect(() => {
    selectedMachineIdRef.current = selectedMachineId;
  }, [selectedMachineId]);

  useEffect(() => {
    selectedShiftIdRef.current = selectedShiftId;
  }, [selectedShiftId]);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    connectWebSocket(tokenRef.current);
    return () => closeWebSocket();
  }, [connectWebSocket, closeWebSocket]);

  const inventoryItemMachineId = (it) => {
    const m = it?.machineId;
    return m?._id?.toString?.() ?? m?._id ?? m ?? "";
  };

  const inventoryProducedByMachine = (items, m) => {
    const mid = m?._id?.toString?.() ?? m?._id ?? "";
    if (!mid) return [];
    return items
      .filter((it) => String(inventoryItemMachineId(it)) === String(mid))
      .map((it) => {
        const part =
          it?.partId && typeof it.partId === "object" ? it.partId : null;
        const partId = String(part?._id || it?.partId || "").trim();
        const code = String(part?.code || it?.code || "").trim();
        const name = String(part?.name || it?.name || "").trim();
        if (!partId || !code) return null;
        return { _id: partId, partId, code, name };
      })
      .filter(Boolean);
  };

  const producedPartsByMachine = useCallback(
    (m) => {
      const mid = m?._id?.toString?.() ?? m?._id ?? "";
      if (!mid) return [];
      const items = (partMachines || [])
        .filter((pm) => {
          const pmMid =
            pm?.machine?._id?.toString?.() ??
            pm?.machine?._id ??
            pm?.machine ??
            "";
          return String(pmMid) === String(mid) && pm?.isActive !== false;
        })
        .map((pm) => pm?.part)
        .filter((p) => p && p.code)
        .map((p) => ({
          _id: p._id,
          partId: p._id,
          code: p.code,
          name: p.name,
        }));
      const map = new Map();
      items.forEach((it) => {
        const k = String(it.code || "").trim();
        if (k && !map.has(k)) map.set(k, it);
      });
      return Array.from(map.values()).sort((a, b) =>
        String(a.code || "").localeCompare(String(b.code || "")),
      );
    },
    [partMachines],
  );

  const machineProductTypeIds = (m) => {
    const fromArr = (m?.productTypes || [])
      .map((c) => String(c || "").trim())
      .filter(Boolean);
    if (fromArr.length) return fromArr;
    // PLC/MQTT sets productType alone
    const single = String(m?.productType || "").trim();
    return single && /^[a-fA-F0-9]{24}$/i.test(single) ? [single] : [];
  };

  const partOptionId = (it) => {
    const raw = it?.partId?._id || it?.partId || it?._id || "";
    const s = String(raw || "").trim();
    // Prefer real Part ObjectIds only (never inventory item ids without partId field)
    if (/^[a-fA-F0-9]{24}$/.test(s)) return s.toLowerCase();
    return "";
  };

  const isPartObjectId = (v) =>
    /^[a-fA-F0-9]{24}$/i.test(String(v || "").trim());

  const normalizeDraftToPartIds = (draft, options) => {
    const out = [];
    const seen = new Set();
    for (const raw of draft || []) {
      const s = String(raw || "").trim();
      if (!s) continue;
      let id = /^[a-fA-F0-9]{24}$/i.test(s) ? s.toLowerCase() : "";
      if (!id) {
        const byCode = options.find(
          (it) => String(it.code || "").toLowerCase() === s.toLowerCase(),
        );
        id = partOptionId(byCode);
      }
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out.slice(0, 2);
  };

  const resolveMachinePartDisplay = (m, producedParts = []) => {
    const ids = machineProductTypeIds(m);
    const options =
      producedPartsByMachine(m).length > 0
        ? producedPartsByMachine(m)
        : inventoryProducedByMachine(inventoryItems, m);
    // Never return raw ObjectId — only human labels
    const nameForId = (id) => {
      const pid = String(id || "")
        .trim()
        .toLowerCase();
      if (!pid) return "";
      const fromDetail = (m?.productTypesDetail || []).find(
        (d) => String(d.id || "").toLowerCase() === pid,
      );
      if (fromDetail) return fromDetail.name || fromDetail.code || "";
      const hit = options.find((it) => partOptionId(it) === pid);
      if (hit) return hit.name || hit.code || "";
      const fromProduced = (producedParts || []).find(
        (p) => String(p?.partId || "").toLowerCase() === pid,
      );
      if (fromProduced) return fromProduced.name || fromProduced.code || "";
      const byCode = options.find(
        (it) => String(it.code) === String(id || "").trim(),
      );
      return byCode?.name || byCode?.code || "";
    };

    const safeLabel = (raw) => {
      const s = String(raw || "").trim();
      if (!s || isPartObjectId(s)) return "";
      return s;
    };

    // Active part (PLC switches this)
    const activeId = String(m?.productType || "").trim();
    const activeName =
      safeLabel(m?.productTypeName) ||
      safeLabel(m?.productTypeCode) ||
      (activeId ? nameForId(activeId) : "") ||
      safeLabel(m?.productName) ||
      "";

    if (ids.length >= 2) {
      const labels =
        (m?.productTypesDetail || []).length > 0
          ? m.productTypesDetail
              .filter((d) =>
                ids.some(
                  (id) =>
                    String(d.id || "").toLowerCase() ===
                    String(id).toLowerCase(),
                ),
              )
              .map((d) => d.name || d.code)
              .filter((x) => safeLabel(x))
          : ids.map((id) => nameForId(id)).filter(Boolean);
      const joined = labels.join(" / ");
      return {
        label: activeName || joined || "—",
        title: joined
          ? `Aktif: ${activeName || "—"} · Yapılandırılan: ${joined}`
          : activeName || undefined,
      };
    }

    if (activeName) {
      return { label: activeName, title: activeName };
    }
    if (safeLabel(m?.productName)) {
      return { label: m.productName, title: m.productName };
    }
    if (ids.length === 1) {
      const name = nameForId(ids[0]);
      return name
        ? { label: name, title: name }
        : { label: "—", title: undefined };
    }
    const fromProduction = (producedParts || [])
      .map((p) => p?.name || p?.code)
      .filter((x) => safeLabel(x));
    if (fromProduction.length) {
      const label = fromProduction.join(" / ");
      return { label, title: label };
    }
    return { label: "—", title: undefined };
  };

  const toggleProductTypeDraft = (partId) => {
    const id = String(partId || "")
      .trim()
      .toLowerCase();
    if (!id) return;
    setProductTypeDraftCodes((prev) => {
      const has = prev.some((x) => x === id);
      if (has) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return prev;
      return [...prev, id];
    });
  };

  const cancelPartEdit = () => {
    setPartEditOpen(false);
    setPartSearchQuery("");
    setProductTypeDraftCodes([]);
    setProductTypePage(1);
  };

  const openPartEdit = async () => {
    const m = stats?.machine;
    if (!m) return;
    setPartSearchQuery("");
    setProductTypePage(1);
    setPartEditOpen(true);

    let nextPartMachines = partMachines;
    let nextInventory = inventoryItems;

    if (!nextPartMachines.length) {
      try {
        const token = getToken();
        const res = await axios.get(`${API_URL}/api/part-machines`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        nextPartMachines = res.data?.partMachines || [];
        setPartMachines(nextPartMachines);
      } catch (e) {
        console.error("Failed to fetch part-machines", e);
      }
    }

    if (!nextInventory.length) {
      try {
        const token = getToken();
        const res = await axios.get(`${API_URL}/api/inventory`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        nextInventory = (res.data?.items || []).filter(
          (it) => it?.partId || it?.code,
        );
        nextInventory.sort((a, b) => {
          const ac = a?.partId?.code || a?.code || "";
          const bc = b?.partId?.code || b?.code || "";
          return String(ac).localeCompare(String(bc));
        });
        setInventoryItems(nextInventory);
      } catch (e) {
        console.error("Failed to fetch inventory", e);
      }
    }

    // Build options from freshly loaded lists (not stale state)
    const mid = m?._id?.toString?.() ?? m?._id ?? "";
    const pmParts = (nextPartMachines || [])
      .filter((pm) => {
        const pmMid =
          pm?.machine?._id?.toString?.() ??
          pm?.machine?._id ??
          pm?.machine ??
          "";
        return String(pmMid) === String(mid) && pm?.isActive !== false;
      })
      .map((pm) => pm?.part)
      .filter((p) => p && p.code)
      .map((p) => ({
        _id: p._id,
        partId: p._id,
        code: p.code,
        name: p.name,
      }));
    const options =
      pmParts.length > 0
        ? pmParts
        : inventoryProducedByMachine(nextInventory, m);

    const configured = machineProductTypeIds(m);
    const draft = configured.length
      ? normalizeDraftToPartIds(configured, options)
      : [];
    setProductTypeDraftCodes(draft);
  };

  const saveMachineProductType = async () => {
    const m = stats?.machine;
    if (!m?._id) return;
    const mid = m._id?.toString?.() ?? m._id;
    const options =
      producedPartsByMachine(m).length > 0
        ? producedPartsByMachine(m)
        : inventoryProducedByMachine(inventoryItems, m);
    const ids = normalizeDraftToPartIds(productTypeDraftCodes, options);
    if (productTypeDraftCodes.length && !ids.length) {
      console.error("Failed to resolve selected parts to Part ids");
      return;
    }
    setSavingProductType(true);
    try {
      const token = getToken();
      const res = await axios.put(
        `${API_URL}/api/machine-live/${mid}/product-types`,
        {
          productTypes: ids,
          productType: ids[0] || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const saved = res.data?.machine || {};
      const names =
        (saved.productTypesDetail || [])
          .map((d) => d.name || d.code)
          .filter(Boolean).length > 0
          ? (saved.productTypesDetail || [])
              .map((d) => d.name || d.code)
              .filter(Boolean)
          : ids
              .map((id) => {
                const hit = options.find((it) => partOptionId(it) === id);
                return hit?.name || hit?.code || "";
              })
              .filter(Boolean);
      setStats((prev) =>
        prev
          ? {
              ...prev,
              machine: {
                ...prev.machine,
                ...saved,
                productTypes: saved.productTypes || ids,
                productType: saved.productType || ids[0] || null,
                productTypesDetail: saved.productTypesDetail || null,
                productName:
                  saved.productName ||
                  (names.length ? names.join(" / ") : null),
              },
              alternatingProduction:
                (saved.productTypes || ids).length >= 2 ||
                prev.alternatingProduction,
            }
          : prev,
      );
      cancelPartEdit();
      fetchStats(selectedMachineId, selectedShiftId, selectedDate);
    } catch (e) {
      console.error("Failed to save product type", e);
    } finally {
      setSavingProductType(false);
    }
  };

  // Live duration: server snapshot + one live leg (work OR stop, never both).
  const getLiveDurations = () => {
    const baseElapsed = stats?.time?.elapsedMs || 0;
    const baseStop = stats?.time?.stopMs || 0;
    const baseWork =
      stats?.time?.workMs != null
        ? stats.time.workMs
        : Math.max(0, baseElapsed - baseStop);

    if (!isLiveDay) {
      return { elapsedMs: baseElapsed, stopMs: baseStop, workMs: baseWork };
    }
    if (!stats?.selectedShift?.startTime) {
      return { elapsedMs: 0, stopMs: 0, workMs: 0 };
    }

    const nowMs = Date.now() + serverOffsetRef.current;
    const segments = stats.plannedSegments || [];

    let elapsedMs;
    if (segments.length > 0) {
      elapsedMs = plannedElapsedMsFromSegments(nowMs, segments);
    } else {
      const shiftStartMs = new Date(stats.selectedShift.startTime).getTime();
      const shiftEndMs = new Date(stats.selectedShift.endTime).getTime();
      if (nowMs < shiftStartMs) elapsedMs = 0;
      else if (nowMs > shiftEndMs)
        elapsedMs = (stats.time?.shiftDurationMinutes || 0) * 60 * 1000;
      else elapsedMs = nowMs - shiftStartMs;
    }

    const serverTimeMs = stats.serverTime
      ? new Date(stats.serverTime).getTime()
      : nowMs;
    const liveDelta = Math.max(0, nowMs - serverTimeMs);

    const activePlanned = (() => {
      if (stats.time?.activePlannedStop?.startTime) {
        return stats.time.activePlannedStop;
      }
      const list =
        stats.stopLogs ||
        stats.topStopsLast ||
        stats.topStopsBiggest ||
        stats.topStops ||
        [];
      const fromList = list.find((s) => {
        if (!s?.isPlanned || !s.startTime) return false;
        const ps = new Date(s.startTime).getTime();
        const pe = s.endTime ? new Date(s.endTime).getTime() : Infinity;
        return nowMs >= ps && nowMs <= pe;
      });
      if (!fromList) return null;
      return {
        startTime: fromList.startTime,
        endTime: fromList.endTime || null,
      };
    })();

    const insideActivePlannedStop =
      activePlanned?.startTime &&
      nowMs >= new Date(activePlanned.startTime).getTime() &&
      (!activePlanned.endTime ||
        nowMs <= new Date(activePlanned.endTime).getTime());

    if (insideActivePlannedStop) {
      return { elapsedMs, stopMs: elapsedMs, workMs: 0 };
    }

    const isEpcMachine = stats?.machine?.entryType === "epc";
    if (isEpcMachine) {
      const stopMs = Math.min(baseStop, elapsedMs);
      return {
        elapsedMs,
        stopMs,
        workMs: Math.max(0, elapsedMs - stopMs),
      };
    }

    const netSec = stats.partMachineCycle?.netCycleTimeSeconds || 1;
    const graceMs = (Math.max(netSec, 1) + 10) * 1000;
    const lastAtMs = stats.production?.lastAt
      ? new Date(stats.production.lastAt).getTime()
      : null;
    const grossStartMs = stats.time?.grossShiftStart
      ? new Date(stats.time.grossShiftStart).getTime()
      : null;
    const inPlanned =
      segments.length === 0 ||
      segments.some((seg) => {
        const a = new Date(seg.start).getTime();
        const b = new Date(seg.end).getTime();
        return nowMs >= a && nowMs < b;
      });
    const inWorkingWindow =
      inPlanned && lastAtMs != null && nowMs <= lastAtMs + graceMs;

    let stopMs;
    let workMs;

    if (!inPlanned) {
      stopMs = Math.min(baseStop, elapsedMs);
      workMs = Math.max(0, elapsedMs - stopMs);
    } else if (
      (stats.production?.total ?? 0) === 0 &&
      grossStartMs != null &&
      nowMs > grossStartMs + graceMs
    ) {
      stopMs = elapsedMs;
      workMs = 0;
    } else if (inWorkingWindow) {
      workMs = Math.min(baseWork + liveDelta, elapsedMs);
      stopMs = Math.max(0, elapsedMs - workMs);
    } else {
      stopMs = Math.min(baseStop + liveDelta, elapsedMs);
      workMs = Math.max(0, elapsedMs - stopMs);
    }

    return { elapsedMs, stopMs, workMs };
  };

  const { elapsedMs, stopMs, workMs } = getLiveDurations();
  const liveAvailability =
    elapsedMs > 0 ? Math.round((workMs / elapsedMs) * 1000) / 10 : 100;

  const units = stats?.production?.total ?? 0;
  const pmc = stats?.partMachineCycle;
  let livePerformance = null;
  if (
    pmc?.netCycleTimeSeconds > 0 &&
    pmc?.cycleQuantity > 0 &&
    units > 0 &&
    workMs > 0
  ) {
    const idealMs =
      (units / pmc.cycleQuantity) * pmc.netCycleTimeSeconds * 1000;
    livePerformance = Math.round((idealMs / workMs) * 1000) / 10;
  }

  const liveQuality = stats?.oee?.quality ?? 100;
  const liveOEE =
    livePerformance != null
      ? Math.round(
          (liveAvailability / 100) *
            (livePerformance / 100) *
            (liveQuality / 100) *
            1000,
        ) / 10
      : Math.round(liveAvailability * 10) / 10;

  const listMachineEntry = machines.find(
    (m) => String(m._id) === String(selectedMachineId),
  );
  const machine = stats?.machine
    ? {
        ...stats.machine,
        entryType:
          stats.machine.entryType || listMachineEntry?.entryType || "device",
      }
    : listMachineEntry || null;
  const donutPercent = elapsedMs > 0 ? (workMs / elapsedMs) * 100 : 100;

  const assignedPartCycles = stats?.assignedPartCycles || [];
  const configuredPartIds = machine
    ? machineProductTypeIds(machine).map((id) => String(id).toLowerCase())
    : [];
  const activePartId = String(
    machine?.productType || configuredPartIds[0] || "",
  ).toLowerCase();

  const cycleMatchesSelection = (c) => {
    if (!c?.netCycleTimeSeconds) return false;
    const partId = String(c.partId || "").toLowerCase();
    if (activePartId) {
      return (
        partId === activePartId ||
        (c.partCode && c.partCode === machine?.productTypeCode)
      );
    }
    return configuredPartIds.length === 1 && partId === configuredPartIds[0];
  };

  const activePartCycle = (() => {
    if (pmc?.netCycleTimeSeconds > 0 && cycleMatchesSelection(pmc)) {
      return {
        netCycleTimeSeconds: pmc.netCycleTimeSeconds,
        cycleQuantity: pmc.cycleQuantity,
      };
    }
    return assignedPartCycles.find(cycleMatchesSelection) || null;
  })();

  const cancelCycleEdit = () => {
    setCycleEditOpen(false);
    setCycleDraftNet("");
    setCycleDraftQty("1");
    setCycleEditError("");
  };

  const openCycleEdit = () => {
    if (!activePartId) {
      setCycleEditError("Önce parça seçin");
      setCycleEditOpen(true);
      return;
    }
    setCycleDraftNet(
      activePartCycle?.netCycleTimeSeconds > 0
        ? String(activePartCycle.netCycleTimeSeconds)
        : "",
    );
    setCycleDraftQty(
      activePartCycle?.cycleQuantity > 0
        ? String(activePartCycle.cycleQuantity)
        : "1",
    );
    setCycleEditError("");
    setCycleEditOpen(true);
  };

  const saveMachineCycleTime = async () => {
    const m = stats?.machine;
    if (!m?._id || !activePartId) return;

    const nt = Number(cycleDraftNet);
    const cq = Number(cycleDraftQty);
    if (!String(cycleDraftNet ?? "").trim() || Number.isNaN(nt) || nt <= 1) {
      setCycleEditError("Net çevrim süresi 1 sn'den büyük olmalı");
      return;
    }
    if (!String(cycleDraftQty ?? "").trim() || Number.isNaN(cq) || cq < 1) {
      setCycleEditError("Çevrim miktarı en az 1 olmalı");
      return;
    }

    setSavingCycle(true);
    setCycleEditError("");
    try {
      const token = getToken();
      const mid = m._id?.toString?.() ?? m._id;
      await axios.put(
        `${API_URL}/api/machine-live/${mid}/cycle-time`,
        {
          partId: activePartId,
          netCycleTime: nt,
          cycleQuantity: cq,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      cancelCycleEdit();
      fetchStats(selectedMachineId, selectedShiftId, selectedDate);
    } catch (e) {
      setCycleEditError(
        e?.response?.data?.message || "Çevrim süresi güncellenemedi",
      );
      console.error("Failed to save cycle time", e);
    } finally {
      setSavingCycle(false);
    }
  };

  const production = stats?.production;
  const machinePartDisplay = machine
    ? resolveMachinePartDisplay(machine, production?.byPart)
    : { label: "—", title: undefined };
  const shiftInfo = stats?.selectedShift;
  const canMonitorWrite = hasPageWriteAccess("/machine-monitor");
  const scrapEntryDefaults = {
    shiftId: shiftInfo?._id || selectedShiftId || undefined,
    partId: activePartId || undefined,
  };
  const handleEntrySuccess = () => {
    fetchStats(selectedMachineId, selectedShiftId, selectedDate);
  };
  const goToProductionReport = () => {
    if (!selectedMachineId) return;
    const reportDate = selectedDate || todayPeriodStr();
    const reportShiftId = shiftInfo?._id || selectedShiftId || "";
    seedReportFilters({
      machineId: selectedMachineId,
      shiftId: reportShiftId,
      startDate: reportDate,
      endDate: reportDate,
    });
    setProductionDetailsOpen(false);
    navigate("/reports/production/by-part/per-part", {
      state: {
        reportFilters: {
          machineId: selectedMachineId,
          shiftId: reportShiftId,
          startDate: reportDate,
          endDate: reportDate,
        },
      },
    });
  };
  const productionByPart =
    production?.byPart?.length > 0
      ? production.byPart
      : [
          {
            partId: null,
            code: null,
            name: null,
            quantity: production?.total ?? 0,
          },
        ];


  const displayedStops =
    stopsSortMode === "last"
      ? stats?.topStopsLast || []
      : stats?.topStopsBiggest || stats?.topStops || [];
  const stopsTitle =
    stopsSortMode === "last" ? "Vardiyadaki Duruşlar" : "Vardiyadaki Duruşlar";
  const speed = stats?.speed;
  const scheduledGrossEndMs = stats?.time?.scheduledGrossEnd
    ? new Date(stats.time.scheduledGrossEnd).getTime()
    : null;

  // Work state indicator: shift/break/outside shift
  const nowForStateMs = stats?.serverTime
    ? new Date(stats.serverTime).getTime()
    : Date.now() + serverOffsetRef.current;
  const segmentsForState = stats?.plannedSegments || [];
  const shiftStartMs = shiftInfo?.startTime
    ? new Date(shiftInfo.startTime).getTime()
    : null;
  const shiftEndMs = shiftInfo?.endTime
    ? new Date(shiftInfo.endTime).getTime()
    : null;

  const activePartCapacity = useMemo(() => {
    if (!activePartCycle || !(activePartCycle.netCycleTimeSeconds > 0) || !shiftStartMs || !shiftEndMs) return null;
    
    let shiftSecondsTotal = 0;
    const segments = stats?.plannedSegments || [];
    if (segments.length > 0) {
      shiftSecondsTotal = Math.max(0, plannedElapsedMsFromSegments(Number.MAX_SAFE_INTEGER, segments) / 1000);
    } else {
      shiftSecondsTotal = Math.max(0, (shiftEndMs - shiftStartMs) / 1000);
    }
    
    return Math.floor(shiftSecondsTotal / activePartCycle.netCycleTimeSeconds) * (activePartCycle.cycleQuantity || 1);
  }, [activePartCycle, shiftStartMs, shiftEndMs, stats?.plannedSegments]);

  const getPartShiftCapacity = useCallback((partId, netDurationMs) => {
    if (!partId) return null;
    const pId = String(partId).toLowerCase();
    
    let cycle = (stats?.assignedPartCycles || []).find((c) => String(c.partId || "").toLowerCase() === pId);
    if (!cycle && machine?.partCycleTimes) {
      cycle = machine.partCycleTimes.find((c) => String(c.partId || "").toLowerCase() === pId);
    }
    
    if (!cycle || !(cycle.netCycleTimeSeconds > 0)) return null;
    
    let shiftSecondsTotal = 0;
    if (typeof netDurationMs === 'number') {
      shiftSecondsTotal = Math.max(0, netDurationMs / 1000);
    } else {
      const segments = stats?.plannedSegments || [];
      if (segments.length > 0) {
        shiftSecondsTotal = Math.max(0, plannedElapsedMsFromSegments(Number.MAX_SAFE_INTEGER, segments) / 1000);
      } else if (shiftStartMs && shiftEndMs) {
        shiftSecondsTotal = Math.max(0, (shiftEndMs - shiftStartMs) / 1000);
      } else {
        return null;
      }
    }
    return Math.floor(shiftSecondsTotal / cycle.netCycleTimeSeconds) * (cycle.cycleQuantity || 1);
  }, [stats?.assignedPartCycles, stats?.plannedSegments, machine?.partCycleTimes, shiftStartMs, shiftEndMs]);
  
  const grossStartMs = stats?.time?.grossShiftStart
    ? new Date(stats.time.grossShiftStart).getTime()
    : null;
  const grossEndMs = stats?.time?.grossShiftEnd
    ? new Date(stats.time.grossShiftEnd).getTime()
    : null;
  const toleranceMs =
    Math.max(0, Number(shiftInfo?.toleranceMinutes) || 0) * 60_000;
  const entryToleranceEndMs =
    shiftStartMs != null ? shiftStartMs + toleranceMs : null;
  const earlyExitToleranceStartMs =
    shiftEndMs != null ? shiftEndMs - toleranceMs : null;
  const lateExitToleranceEndMs = scheduledGrossEndMs ?? grossEndMs;
  const hasStartedProduction = (production?.total ?? 0) > 0;
  const hasActiveStop = !!stats?.time?.ongoingStopStart;
  const isActivelyProducing = hasStartedProduction && !hasActiveStop;

  let workState = null;
  if (!shiftInfo || !shiftStartMs || !shiftEndMs) {
    workState = { kind: "no_shift", label: "Vardiya yok" };
  } else if (nowForStateMs < shiftStartMs) {
    workState = { kind: "outside", label: "Çalışma dışı" };
  } else if (grossEndMs != null && nowForStateMs >= grossEndMs) {
    workState = { kind: "outside", label: "Çalışma dışı" };
  } else if (
    scheduledGrossEndMs != null &&
    grossEndMs != null &&
    nowForStateMs >= scheduledGrossEndMs &&
    nowForStateMs < grossEndMs
  ) {
    workState = { kind: "overtime", label: "Mesai fazlası" };
  } else if (
    toleranceMs > 0 &&
    !hasStartedProduction &&
    entryToleranceEndMs != null &&
    nowForStateMs >= shiftStartMs &&
    nowForStateMs < entryToleranceEndMs
  ) {
    workState = { kind: "tolerance_start", label: "Giriş Toleransı" };
  } else if (
    toleranceMs > 0 &&
    !isActivelyProducing &&
    earlyExitToleranceStartMs != null &&
    nowForStateMs >= earlyExitToleranceStartMs &&
    nowForStateMs < shiftEndMs
  ) {
    workState = { kind: "tolerance_early", label: "Erken Çıkış Toleransı" };
  } else if (
    toleranceMs > 0 &&
    !isActivelyProducing &&
    lateExitToleranceEndMs != null &&
    nowForStateMs >= shiftEndMs &&
    nowForStateMs < lateExitToleranceEndMs
  ) {
    workState = { kind: "tolerance_late", label: "Geç Çıkış Toleransı" };
  } else if (segmentsForState.length > 0) {
    const inPlanned = segmentsForState.some((s) => {
      const a = new Date(s.start).getTime();
      const b = new Date(s.end).getTime();
      return nowForStateMs >= a && nowForStateMs < b;
    });
    workState = inPlanned
      ? { kind: "working", label: "Çalışma" }
      : { kind: "break", label: "Mola" };
  } else {
    workState = { kind: "working", label: "Çalışma" };
  }

  const isMachineOnline = machine?.status === "online";
  const runStateLabel = hasActiveStop
    ? "Duruşta"
    : workState?.kind === "break"
      ? "Mola"
      : isMachineOnline
        ? "Çalışıyor"
        : "Duruşta";

  // Open a new stop log starting at startTimeMs. Skips if one is already open.
  const openNewStop = useCallback(
    async (machineId, startTimeMs) => {
      if (!machineId) return;
      if (statsRef.current?.time?.ongoingStopStart) return; // already open
      try {
        const token = getToken();
        await axios.post(
          `${API_URL}/api/machine-stop-logs`,
          {
            machine: machineId,
            startTime: new Date(
              startTimeMs ?? Date.now() + serverOffsetRef.current,
            ).toISOString(),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const sid = statsRef.current?.selectedShift?._id || "";
        fetchStatsRef.current?.(machineId, sid);
      } catch (e) {
        console.error("Failed to open new stop", e);
      }
    },
    [getToken],
  );

  // Close the ongoing open stop log via API. Returns true on success.
  // Caller is responsible for refreshing stats afterwards.
  const closeOngoingStop = useCallback(
    async (machineId, endTimeMs) => {
      if (!machineId) return false;
      const allStops =
        statsRef.current?.stopLogs ||
        statsRef.current?.topStopsLast ||
        statsRef.current?.topStopsBiggest ||
        statsRef.current?.topStops ||
        [];
      let stopId = allStops.find((s) => !s.endTime)?._id;

      if (!stopId && statsRef.current?.time?.ongoingStopStart) {
        try {
          const token = getToken();
          const res = await axios.get(`${API_URL}/api/machine-stop-logs`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { machineId, limit: 20 },
          });
          const open = (res.data?.stopLogs || []).find((s) => !s.endTime);
          stopId = open?._id;
        } catch (e) {
          console.error("Failed to fetch open stop", e);
          return false;
        }
      }

      if (!stopId) return false;

      try {
        const token = getToken();
        const endTime = new Date(
          endTimeMs ?? Date.now() + serverOffsetRef.current,
        ).toISOString();
        await axios.put(
          `${API_URL}/api/machine-stop-logs/${stopId}`,
          { endTime },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        return true;
      } catch (e) {
        console.error("Failed to close stop", e);
        return false;
      }
    },
    [getToken],
  );

  // Detect workState transitions and act:
  // → break or → outside : close the ongoing stop, then refresh
  const workStateKind = workState?.kind ?? null;
  useEffect(() => {
    if (selectedDateRef.current !== todayPeriodStr()) {
      prevWorkStateKindRef.current = workStateKind;
      return;
    }
    const prev = prevWorkStateKindRef.current;
    const curr = workStateKind;
    if (prev !== null && prev !== curr) {
      const mid = selectedMachineIdRef.current;
      const sid = selectedShiftIdRef.current;
      const nowMs = Date.now() + serverOffsetRef.current;

      if (mid) {
        if (curr === "break" || curr === "outside") {
          closeOngoingStop(mid, nowMs).then((closed) => {
            const stillSafe =
              prevWorkStateKindRef.current === "break" ||
              prevWorkStateKindRef.current === "outside";
            if (closed || stillSafe) {
              fetchStatsRef.current?.(mid, sid);
            }
          });
        } else if (
          prev === "break" ||
          prev === "tolerance_start" ||
          prev === "tolerance_early" ||
          prev === "tolerance_late"
        ) {
          // Let backend sync open stop after cycle + grace — not immediately here.
          fetchStatsRef.current?.(mid, sid);
        }
      }
    }
    prevWorkStateKindRef.current = curr;
  }, [workStateKind, closeOngoingStop]);

  const targetBreakdown = useMemo(() => {
    return productionByPart.map(row => {
      const cap = getPartShiftCapacity(row.partId, row.netDurationMs);
      return {
        ...row,
        target: cap
      };
    }).filter(r => r.target != null).sort((a, b) => b.target - a.target);
  }, [productionByPart, getPartShiftCapacity]);

  const totalTarget = useMemo(() => {
    if (targetBreakdown.length === 0) return activePartCapacity;
    return targetBreakdown.reduce((acc, r) => acc + r.target, 0);
  }, [targetBreakdown, activePartCapacity]);

  return (
    <div className="machine-live-monitor">
      {/* Header */}
      <div className="mlm-header card card-body p-2">
        <div className="mlm-header-left">
          <h2 className="mlm-title">Makine Canlı İzleme</h2>
          {isLiveDay ? (
            <div
              className={`mlm-ws-status ${wsConnected ? "online" : "offline"}`}
            >
              {wsConnected ? <FiWifi size={14} /> : <FiWifiOff size={14} />}
              <span>{wsConnected ? "Canlı" : "Bağlantı Kesildi"}</span>
            </div>
          ) : (
            <div className="mlm-ws-status history">
              <FiClock size={14} />
              <span>Geçmiş</span>
            </div>
          )}
        </div>
        <div className="mlm-header-right">
          <div className="mlm-selector-wrap">
            <label>Makine Seçiniz</label>
            <select
              value={selectedMachineId}
              onChange={(e) => {
                const next = e.target.value;
                setSelectedMachineId(next);
                writeLastMachineId(user, next);
              }}
              className="mlm-select"
            >
              {!selectedMachineId && <option value="">Makine seçiniz</option>}
              {selectedMachineId &&
                !machines.some(
                  (m) => String(m._id) === String(selectedMachineId),
                ) && <option value={selectedMachineId}>Yükleniyor...</option>}
              {machines.map((m) => (
                <option key={m._id} value={m._id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mlm-selector-wrap">
            <label>Vardiya</label>
            <select
              value={selectedShiftId}
              onChange={(e) => setSelectedShiftId(e.target.value)}
              className="mlm-select"
            >
              <option value="">Otomatik</option>
              {shifts.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mlm-selector-wrap">
            <label>Tarih</label>
            <PeriodDateSelector
              dayOnly
              mode="day"
              anchorDate={period.anchorDate}
              startDate={period.startDate}
              endDate={period.endDate}
              onChange={setPeriod}
            />
          </div>
          <button
            className="mlm-refresh-btn"
            onClick={() =>
              fetchStats(selectedMachineId, selectedShiftId, selectedDate)
            }
            disabled={loading}
          >
            <FiRefreshCw className={loading ? "spin" : ""} size={15} />
            Yenile
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="mlm-grid">
        {/* Row 1 */}
        <div className="mlm-card mlm-machine-info">
          <div className="mlm-card-title-row">
            <div className="mlm-card-title mlm-card-title--inline">Makine</div>
            <button
              type="button"
              className={`mlm-status-badge ${machine?.status || "offline"} is-clickable`}
              title="Durum geçmişi"
              onClick={() => setStatusHistoryOpen(true)}
            >
              {isMachineOnline ? "Çevrimiçi" : "Çevrimdışı"}
              {" - "}
              {runStateLabel}
            </button>
          </div>
          <div className="mlm-info-row">
            <span className="mlm-info-label">Adı</span>
            <span className="mlm-info-value">{machine?.name || "—"}</span>
          </div>
          <div className="mlm-info-row">
            <span className="mlm-info-label">Parça Adı</span>
            <span className="mlm-info-value mlm-info-value--part">
              <span className="mlm-part-name" title={machinePartDisplay.title}>
                {machinePartDisplay.label}
              </span>
              {machine?._id && isLiveDay && machine?.entryType !== "epc" && (
                <button
                  type="button"
                  className="mlm-part-edit-btn"
                  title="Parça düzenle"
                  aria-label="Parça düzenle"
                  onClick={openPartEdit}
                >
                  <FiEdit2 size={14} />
                </button>
              )}
            </span>
          </div>
          <div className="mlm-info-row">
            <span className="mlm-info-label">Çevrim Süresi</span>
            <span className="mlm-info-value mlm-info-value--part">
              {activePartCycle?.netCycleTimeSeconds > 0 ? (
                <>
                  {activePartCycle.netCycleTimeSeconds}s
                  {activePartCycle.cycleQuantity > 1
                    ? ` / ${activePartCycle.cycleQuantity} ad`
                    : ""}
                </>
              ) : (
                "—"
              )}
              {machine?._id &&
                isLiveDay &&
                machine?.entryType !== "epc" &&
                activePartId && (
                  <button
                    type="button"
                    className="mlm-part-edit-btn"
                    title="Çevrim süresi düzenle"
                    aria-label="Çevrim süresi düzenle"
                    onClick={openCycleEdit}
                  >
                    <FiEdit2 size={14} />
                  </button>
                )}
            </span>
          </div>
          <div className="mlm-info-row">
            <span className="mlm-info-label">Konum</span>
            <span className="mlm-info-value">{machine?.location || "—"}</span>
          </div>
        </div>

        <div className="mlm-card mlm-shift-info">
          <div className="mlm-card-title">Vardiya</div>
          {shiftInfo ? (
            <>
              <div className="mlm-shift-name">
                {shiftInfo.name}
                {workState?.kind && workState.kind !== "working" && (
                  <span className={`mlm-workstate-badge ${workState.kind}`}>
                    {workState.label}
                  </span>
                )}
              </div>
              <div className="mlm-shift-time-row">
                <div>
                  <div className="mlm-shift-time-label">
                    Başlangıç - Bitiş Saati
                  </div>
                  <div className="mlm-shift-time">
                    {shiftInfo.scheduleStartTime != null &&
                    shiftInfo.scheduleEndTime != null
                      ? `${shiftInfo.scheduleStartTime} – ${shiftInfo.scheduleEndTime}`
                      : `${formatTimeTrInAppTz(shiftInfo.startTime)} – ${formatTimeTrInAppTz(shiftInfo.endTime)}`}
                  </div>
                </div>
                <button
                  type="button"
                  className="mlm-breaks-btn"
                  title="Molalar"
                  aria-label="Molalar"
                  onClick={() => setBreaksModalOpen(true)}
                >
                  <FiClock size={12} />
                  {(shiftInfo.breaks || []).length > 0 && (
                    <span className="mlm-breaks-count">
                      {shiftInfo.breaks.length}
                    </span>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="mlm-no-shift">
              Vardiya seçilmedi{" "}
              {workState?.kind && workState.kind !== "working" && (
                <span className={`mlm-workstate-badge ${workState.kind}`}>
                  {workState.label}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="mlm-card mlm-production-count">
          <div className="mlm-card-title-row">
            <div className="mlm-card-title mlm-card-title--inline">
              Vardiya Üretim Miktarı
            </div>
            <button
              type="button"
              className="mlm-card-detail-btn"
              title="Üretim detayı"
              aria-label="Üretim detayı"
              disabled={!selectedMachineId}
              onClick={() => setProductionDetailsOpen(true)}
            >
              <FiInfo size={13} />
            </button>
          </div>
          <div className="mlm-production-produced">
            <div className="mlm-production-produced-header">
              <div className="mlm-production-section-label">
                Üretilen (Adet)
              </div>
            </div>
            <div className="mlm-production-parts" role="list">
              {productionByPart.map((row, idx) => {
                const label = row.name || row.code || "—";
                const title =
                  [row.code, row.name]
                    .filter(Boolean)
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .join(" — ") || label;
                const partKey = row.partId || `part-${idx}`;
                const isActive =
                  row.partId &&
                  String(row.partId).toLowerCase() ===
                    String(machine?.productType || "").toLowerCase();
                const partCapacity = getPartShiftCapacity(row.partId, row.netDurationMs);
                
                return (
                  <div
                    key={partKey}
                    className={`mlm-production-part-item${isActive ? " is-active" : ""}`}
                    role="listitem"
                    title={title}
                  >
                    <div className="mlm-production-part-qty">
                      {row.quantity ?? 0}
                    </div>
                    <div className="mlm-production-part-name">{label}</div>
                    {partCapacity != null && (
                      <div style={{ fontSize: "10px", color: "var(--m-text-subtle, #94a3b8)", marginTop: "2px", fontWeight: 500 }}>
                        Hedef: {partCapacity}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mlm-divider" />
          <div className="mlm-production-scrap">
            <div className="mlm-production-scrap-main">
              <div className="mlm-production-section-label">Hurda (Adet)</div>
              <div className="mlm-big-number secondary mlm-scrap-number">
                {production?.rejected ?? 0}
              </div>
            </div>
            {canMonitorWrite && machine?._id && (
              <button
                type="button"
                className="mlm-entry-add-btn mlm-entry-add-btn--scrap"
                title="Hurda girişi yap"
                aria-label="Hurda girişi yap"
                onClick={() => setEntryModal({ type: "scrap" })}
              >
                <FiPlus size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="mlm-card mlm-last-hour" style={{ position: 'relative', padding: 0 }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
            <div className="mlm-card-title" style={{ marginBottom: '16px' }}>Vardiya Üretim Hedefi</div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
            {targetBreakdown.length > 0 ? targetBreakdown.map((r, idx) => {
              const label = r.name || r.code || "—";
              const isActive = r.partId && String(r.partId).toLowerCase() === String(machine?.productType || "").toLowerCase();
              const startTimeLabel = r.startAt ? formatTimeTrInAppTz(new Date(r.startAt)) : null;
              
              return (
                <div 
                  key={r.partId || idx} 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: isActive ? '4px 8px' : '2px 4px',
                    background: isActive ? 'rgba(0, 201, 183, 0.08)' : 'transparent',
                    border: isActive ? '1px solid rgba(0, 201, 183, 0.2)' : '1px solid transparent',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <span 
                    style={{ 
                      color: isActive ? 'var(--m-text-primary, #e2e8f0)' : 'var(--m-text-subtle, #94a3b8)',
                      fontSize: isActive ? '0.85rem' : '0.8rem',
                      fontWeight: isActive ? 500 : 400,
                      textOverflow: 'ellipsis', 
                      overflow: 'hidden', 
                      whiteSpace: 'nowrap', 
                      paddingRight: '12px' 
                    }} 
                    title={label}
                  >
                    {label}
                    {/* {startTimeLabel && (
                      <span style={{
                        display: 'block',
                        fontSize: '0.7rem',
                        color: 'var(--m-text-subtle, #64748b)',
                        fontWeight: 400,
                        marginTop: '1px',
                      }}>
                        {startTimeLabel}&apos;den
                      </span>
                    )} */}
                  </span>
                  <span 
                    style={{ 
                      color: isActive ? '#00c9b7' : '#38bdf8',
                      fontSize: isActive ? '0.95rem' : '0.9rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {r.target}
                  </span>
                </div>
              );
            }) : (
              <div style={{ color: 'var(--m-text-subtle)', textAlign: 'center', marginTop: '20px' }}>—</div>
            )}
          </div>
          </div>
        </div>

        {/* Row 2 */}
        <div className="mlm-card mlm-machine-status">
          <div className="mlm-card-title">Makine Durumu</div>
          <div className="mlm-donut-container">
            <DonutChart
              percentage={donutPercent}
              size={160}
              color="#00c9b7"
              label={formatDuration(workMs)}
            />
          </div>
          <div className="mlm-donut-legend">
            <span
              className="mlm-legend-dot"
              style={{ background: "#00c9b7" }}
            />
            <span>Vardiya Çalışma Süresi</span>
          </div>
        </div>

        <div className="mlm-card mlm-time-stats">
          <div className="mlm-card-title">Duruş / Çalışma / Toplam Süre</div>
          <div className="mlm-time-row">
            <div className="mlm-time-value">{formatDuration(stopMs)}</div>
            <div className="mlm-time-label">
              Vardiyadaki Toplam Duruş Süresi
            </div>
          </div>
          <div className="mlm-time-row">
            <div className="mlm-time-value">{formatDuration(workMs)}</div>
            <div className="mlm-time-label">
              Vardiyadaki Toplam Çalışma Süresi
            </div>
          </div>
          <div className="mlm-time-row">
            <div className="mlm-time-value">{formatDuration(elapsedMs)}</div>
            <div className="mlm-time-label">Vardiya Toplam Süre</div>
          </div>
        </div>

        <div className="mlm-card mlm-top-stops">
          <div className="mlm-card-title-row">
            <div className="mlm-card-title mlm-card-title--inline">
              {stopsTitle}
            </div>
            <div
              className="mlm-stops-sort-toggle"
              role="group"
              aria-label="Duruş listesi sıralaması"
            >
              <button
                type="button"
                className={`mlm-stops-sort-btn${stopsSortMode === "biggest" ? " active" : ""}`}
                onClick={() => setStopsSortMode("biggest")}
              >
                En Büyük
              </button>
              <button
                type="button"
                className={`mlm-stops-sort-btn${stopsSortMode === "last" ? " active" : ""}`}
                onClick={() => setStopsSortMode("last")}
              >
                Son
              </button>
            </div>
          </div>
          {displayedStops.length === 0 ? (
            <div className="mlm-empty-stops">
              Bu vardiyadaki duruş kaydı yok
            </div>
          ) : (
            <div className="mlm-stops-table-wrap">
              <table className="mlm-stops-table">
                <thead>
                  <tr>
                    <th>Duruş Adı</th>
                    <th>Başlangıç Tarihi</th>
                    <th>Bitiş Tarihi</th>
                    <th>Duruş Süresi</th>
                    <th>Dakika</th>
                    {canMonitorWrite && <th className="mlm-stops-action-col" />}
                  </tr>
                </thead>
                <tbody>
                  {displayedStops.map((stop, idx) => (
                    <tr key={stop._id || idx}>
                      <td>{stop.stopReason?.name || "Belirsiz Duruş"}</td>
                      <td>{formatDateTimeTrInAppTz(stop.startTime)}</td>
                      <td>
                        {stop.endTime ? (
                          formatDateTimeTrInAppTz(stop.endTime)
                        ) : (
                          <span className="mlm-ongoing">Devam ediyor</span>
                        )}
                      </td>
                      <td>{formatDuration(stop.durationSeconds * 1000)}</td>
                      <td>{stop.durationMinutes}</td>
                      {canMonitorWrite && (
                        <td className="mlm-stops-action-col">
                          {stop._id && (
                            <button
                              type="button"
                              className="mlm-entry-add-btn mlm-entry-add-btn--stop"
                              title="Bu duruş için giriş yap"
                              aria-label="Bu duruş için giriş yap"
                              onClick={() =>
                                setEntryModal({
                                  type: "stop",
                                  stopLogId: stop._id,
                                  stopDate: stop.startTime
                                    ? dateStrInAppTz(new Date(stop.startTime))
                                    : undefined,
                                })
                              }
                            >
                              <FiPlus size={14} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Row 3 */}
        <div className="mlm-card mlm-oee">
          <div className="mlm-card-title">OEE</div>
          <div className="mlm-gauges-row">
            <GaugeCircle
              value={liveOEE}
              max={100}
              label="OEE"
              color="#00c9b7"
            />
            <GaugeCircle
              value={liveAvailability}
              max={100}
              label="Kullanılabilirlik"
              color="#00c9b7"
            />
            <GaugeCircle
              value={livePerformance}
              max={100}
              label="Performans"
              color={livePerformance != null ? "#00c9b7" : "#94a3b8"}
            />
            <GaugeCircle
              value={liveQuality}
              max={100}
              label="Kalite"
              color="#00c9b7"
            />
          </div>
        </div>

        <div className="mlm-card mlm-speed-nostop">
          <div className="mlm-card-title">
            Vardiya Hız (Duruşsuz) <small>DAKİKA/ADET</small>
          </div>
          <div className="mlm-big-number">
            {speed?.noStopSpeed != null ? speed.noStopSpeed : "—"}
          </div>
          <div className="mlm-big-number-label">Duruşsuz Hız</div>
        </div>

        <div className="mlm-card mlm-speed-total">
          <div className="mlm-card-title">
            Vardiya Hız (Toplam) <small>DAKİKA/ADET</small>
          </div>
          <div className="mlm-big-number">
            {speed?.totalSpeed != null ? speed.totalSpeed : "—"}
          </div>
          <div className="mlm-big-number-label">Toplam Hız</div>
        </div>
      </div>

      <Modal
        isOpen={partEditOpen}
        width="medium"
        scrollBehavior="inside"
        onClose={savingProductType ? undefined : cancelPartEdit}
        className="device-monitor-product-type-modal"
      >
        <ModalHeader>Parça seçimi</ModalHeader>
        <ModalBody
          className="device-monitor-product-type-modal__body"
          showCloseButton={false}
        >
          {(() => {
            if (!machine) return null;

            const PAGE_SIZE = 10;
            const q = partSearchQuery.trim().toLowerCase();
            const machineParts =
              producedPartsByMachine(machine).length > 0
                ? producedPartsByMachine(machine)
                : inventoryProducedByMachine(inventoryItems, machine);
            const filtered = q
              ? machineParts.filter((it) => {
                  const c = String(it.code || "").toLowerCase();
                  const n = String(it.name || "").toLowerCase();
                  return c.includes(q) || n.includes(q);
                })
              : machineParts;
            const totalItems = filtered.length;
            const pageCount = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
            const safePage = Math.min(Math.max(1, productTypePage), pageCount);
            const rows = filtered.slice(
              (safePage - 1) * PAGE_SIZE,
              safePage * PAGE_SIZE,
            );
            const selectedParts = productTypeDraftCodes.map((id) => {
              const part = machineParts.find(
                (it) => partOptionId(it) === String(id),
              );
              return {
                id,
                code: part?.code || id,
                name: part?.name || null,
              };
            });

            return (
              <div className="device-monitor-product-type-list">
                <div className="device-monitor-product-type-list__selected">
                  <span className="device-monitor-product-type-list__selected-label">
                    Seçili parçalar
                  </span>
                  {selectedParts.length === 0 ? (
                    <span className="device-monitor-product-type-list__selected-empty">
                      Parça seçilmedi
                    </span>
                  ) : (
                    <div className="device-monitor-product-type-list__selected-chips">
                      {selectedParts.map((part) => {
                        const chipLabel = [part.code, part.name]
                          .filter(Boolean)
                          .join(" — ");
                        return (
                          <span
                            key={part.id}
                            className="device-monitor-product-type-list__selected-chip"
                            title={chipLabel}
                          >
                            <span className="device-monitor-product-type-list__code">
                              {part.code}
                            </span>
                            {part.name ? (
                              <span className="device-monitor-product-type-list__name">
                                {part.name}
                              </span>
                            ) : null}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="device-monitor-product-type-list__search">
                  <FiSearch
                    size={16}
                    aria-hidden
                    className="device-monitor-product-type-list__search-icon"
                  />
                  <input
                    type="search"
                    className="device-monitor-product-type-list__search-input"
                    value={partSearchQuery}
                    onChange={(e) => {
                      setPartSearchQuery(e.target.value);
                      setProductTypePage(1);
                    }}
                    placeholder="Ara…"
                    aria-label="Ara"
                    autoComplete="off"
                    autoFocus
                    disabled={savingProductType}
                  />
                </div>

                <div
                  className="device-monitor-product-type-list__items"
                  role="listbox"
                  aria-label="Parça"
                >
                  {rows.length === 0 ? (
                    <div className="device-monitor-product-type-list__empty">
                      Sonuç bulunamadı
                    </div>
                  ) : (
                    rows.map((it) => {
                      const id = partOptionId(it);
                      const isSelected = productTypeDraftCodes.some(
                        (x) =>
                          String(x).toLowerCase() === String(id).toLowerCase(),
                      );
                      const atMax =
                        productTypeDraftCodes.length >= 2 && !isSelected;
                      const label = [it.code, it.name]
                        .filter(Boolean)
                        .join(" — ");
                      return (
                        <button
                          key={id || it.code}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          title={label}
                          disabled={savingProductType || atMax || !id}
                          className={`device-monitor-product-type-list__item${
                            isSelected ? " is-selected" : ""
                          }`}
                          onClick={() => toggleProductTypeDraft(id)}
                        >
                          <span className="device-monitor-product-type-list__code">
                            {it.code}
                          </span>
                          {it.name ? (
                            <span className="device-monitor-product-type-list__name">
                              {it.name}
                            </span>
                          ) : null}
                        </button>
                      );
                    })
                  )}
                </div>

                <Pagination
                  totalItems={totalItems}
                  totalPerPage={PAGE_SIZE}
                  currentPage={safePage}
                  options={{
                    selectQuantity: false,
                    simpleCounting: true,
                    skipPrevious: true,
                    skipNext: true,
                    previous: true,
                    next: true,
                  }}
                  onPageChange={({ pagination }) => {
                    setProductTypePage(pagination?.currentPage || 1);
                  }}
                />
              </div>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            disabled={savingProductType}
            onClick={cancelPartEdit}
          >
            İptal
          </Button>
          <Button
            appearance="primary-animated"
            type="button"
            disabled={savingProductType || !machine}
            onClick={saveMachineProductType}
          >
            Kaydet
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={cycleEditOpen}
        width="small"
        onClose={savingCycle ? undefined : cancelCycleEdit}
        className="mlm-cycle-edit-modal"
      >
        <ModalHeader>Çevrim süresi</ModalHeader>
        <ModalBody showCloseButton={false}>
          {machinePartDisplay.label && machinePartDisplay.label !== "—" ? (
            <div className="mlm-cycle-edit-part">
              {machinePartDisplay.label}
            </div>
          ) : null}
          {!activePartId ? (
            <div className="mlm-cycle-edit-error">Önce parça seçin</div>
          ) : (
            <div className="mlm-cycle-edit-form">
              <div className="mlm-cycle-edit-field">
                <Label required>Net çevrim (sn)</Label>
                <Input
                  type="number"
                  min="2"
                  step="any"
                  value={cycleDraftNet}
                  onChange={setCycleDraftNet}
                  disabled={savingCycle}
                />
              </div>
              <div className="mlm-cycle-edit-field">
                <Label required>Çevrim miktarı (ad)</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={cycleDraftQty}
                  onChange={setCycleDraftQty}
                  disabled={savingCycle}
                />
              </div>
            </div>
          )}
          {cycleEditError ? (
            <div className="mlm-cycle-edit-error">{cycleEditError}</div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            disabled={savingCycle}
            onClick={cancelCycleEdit}
          >
            İptal
          </Button>
          <Button
            appearance="primary-animated"
            type="button"
            disabled={savingCycle || !machine || !activePartId}
            onClick={saveMachineCycleTime}
          >
            Kaydet
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={breaksModalOpen}
        onClose={() => setBreaksModalOpen(false)}
        width="small"
      >
        <ModalHeader>
          {shiftInfo?.name ? `${shiftInfo.name} — Molalar` : "Molalar"}
        </ModalHeader>
        <ModalBody className="mlm-breaks-modal-body" showCloseButton={false}>
          {(shiftInfo?.breaks || []).length === 0 ? (
            <div className="mlm-breaks-empty">
              Bu vardiya için mola tanımlı değil
            </div>
          ) : (
            <div className="mlm-breaks-list">
              {(shiftInfo?.breaks || []).map((br, idx) => (
                <div key={br._id || idx} className="mlm-break-row">
                  <span className="mlm-break-name">{br.name}</span>
                  <span className="mlm-break-time">
                    {br.startTime} – {br.endTime}
                  </span>
                </div>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="primary-subtle"
            onClick={() => setBreaksModalOpen(false)}
          >
            Kapat
          </Button>
        </ModalFooter>
      </Modal>

      <ProductionDetailsModal
        isOpen={productionDetailsOpen}
        onClose={() => setProductionDetailsOpen(false)}
        machine={machine}
        from={stats?.time?.grossShiftStart || shiftInfo?.startTime}
        to={
          stats?.time?.grossShiftEnd ||
          shiftInfo?.endTime ||
          new Date().toISOString()
        }
        activePartId={activePartId}
        getToken={getToken}
        onOpenReport={goToProductionReport}
        canOpenReport={!!selectedMachineId}
      />
      <ProductionEntryModal
        isOpen={entryModal?.type === "production"}
        onClose={() => setEntryModal(null)}
        machine={machine}
        initialPartId={scrapEntryDefaults.partId}
        getToken={getToken}
        onSuccess={handleEntrySuccess}
      />
      <ScrapEntryModal
        isOpen={entryModal?.type === "scrap"}
        onClose={() => setEntryModal(null)}
        machine={machine}
        initialShiftId={scrapEntryDefaults.shiftId}
        initialPartId={scrapEntryDefaults.partId}
        getToken={getToken}
        onSuccess={handleEntrySuccess}
      />
      <StopReasonModal
        isOpen={entryModal?.type === "stop"}
        onClose={() => setEntryModal(null)}
        machine={machine}
        initialStopLogId={entryModal?.stopLogId}
        initialStopDate={entryModal?.stopDate}
        getToken={getToken}
        onSuccess={handleEntrySuccess}
      />
      <StatusHistoryModal
        isOpen={statusHistoryOpen}
        onClose={() => setStatusHistoryOpen(false)}
        machine={machine}
        getToken={getToken}
      />
    </div>
  );
};

export default MachineMonitor;
